import React, { useState, useEffect, useRef } from "react";
import { Amplify } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession, signOut as amplifySignOut } from 'aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';
import { setUnauthorizedHandler } from "./api";

import MainLayout from "./layouts/MainLayout";
import DatabaseSetupPage from "./pages/DatabaseSetupPage";
import DataUploadPage from "./pages/DataUploadPage";
import AIInsightsPage from "./pages/AIInsightsPage";
import DashboardPage from "./pages/DashboardPage";
import { BoardActivityProvider } from "./insights/BoardActivityContext";
import { BlueprintImportProvider } from "./insights/BlueprintImportContext";
import { UploadQueueProvider } from "./UploadQueueContext";
import S3UploadPage from "./pages/S3UploadPage";
import AdminPage from "./pages/AdminPage";
import SecurityPage from "./pages/SecurityPage";
import FeedbackWidget from "./components/FeedbackWidget";
import TermsGate from "./components/TermsGate";
import { FeatureFlagsContext } from "./featureFlags";
import { ProjectProvider } from "./ProjectContext";
import { APP_VERSION } from "./version";
import logo from "./BoomerangLogoA.jpg";

// Bulletproof: Strip accidental quotes or spaces from the .env variables
const USER_POOL_ID = (process.env.REACT_APP_USER_POOL_ID || "").replace(/^["']|["']$/g, "").trim();
const USER_POOL_CLIENT_ID = (process.env.REACT_APP_USER_POOL_CLIENT_ID || "").replace(/^["']|["']$/g, "").trim();

// Configure Amplify to use your real AWS Cognito User Pool
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: USER_POOL_ID,
      userPoolClientId: USER_POOL_CLIENT_ID,
    }
  }
});

// ── Idle-session policy ──────────────────────────────────────────────
// The idle timeout must survive browser refreshes / restarts. Amplify stores
// tokens in localStorage (refresh token valid up to 30 days) and will silently
// restore a session on load, so a purely in-memory timer is not enough — we
// also persist the last-activity timestamp and enforce it on every load.
const INACTIVITY_MS = 2 * 60 * 60 * 1000; // 2 hours
const LAST_ACTIVITY_KEY = "boomerang_last_activity";

function readLastActivity() {
  try { return parseInt(window.localStorage.getItem(LAST_ACTIVITY_KEY) || "0", 10) || 0; }
  catch (e) { return 0; }
}
function writeLastActivity(ts) {
  try { window.localStorage.setItem(LAST_ACTIVITY_KEY, String(ts)); } catch (e) { /* ignore */ }
}
function clearLastActivity() {
  try { window.localStorage.removeItem(LAST_ACTIVITY_KEY); } catch (e) { /* ignore */ }
}

export default function App() {
  // True while signing out — shows a full-screen overlay so the Amplify login
  // screen never flashes between sign-out and the redirect to the marketing site.
  const [signingOut, setSigningOut] = useState(false);

  // Inject a custom header into the AWS Amplify login screen
  const authComponents = {
    Header() {
      return (
        <div className="auth-brand-lockup">
          <img src={logo} alt="Boomerang Logo" className="auth-brand-logo" />
          <h2 className="auth-brand-title">Boomerang Data Intelligence</h2>
          <p className="auth-brand-sub">Intelligent Data Ingestion Platform</p>
        </div>
      );
    }
  };

  return (
    <>
      {signingOut && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 2147483647,
          background: 'linear-gradient(135deg, #0f172a 0%, #1a1040 50%, #0f172a 100%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px',
        }}>
          <img src={logo} alt="Boomerang" style={{ height: '52px', objectFit: 'contain' }} />
          <span style={{ color: '#94a3b8', fontSize: '14px' }}>Signing out…</span>
        </div>
      )}
      <Authenticator hideSignUp={true} components={authComponents}>
        {({ signOut, user }) => {
          const handleSignOut = async () => {
            setSigningOut(true); // cover the screen immediately — hides the login flash
            try { await amplifySignOut(); } catch (e) { /* ignore */ }
            // Prod returns to the marketing site; non-prod (Dev/local) stays in this app
            // (reload to the sign-in screen) so we don't bounce to the Prod domain.
            const _isProdEnv = (process.env.REACT_APP_ENV || "production").toLowerCase() === "production";
            window.location.href = _isProdEnv ? "https://boomerangaidata.com" : "/";
          };
          return <MainApp signOut={handleSignOut} cognitoUser={user} />;
        }}
      </Authenticator>
    </>
  );
}

function MainApp({ signOut, cognitoUser }) {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [jwtToken, setJwtToken] = useState(null);
  const [tenantType, setTenantType] = useState(null); // null = loading
  const [aiMessages, setAiMessages] = useState([]); // Persist AI chat history across tab switches
  // Which project the persisted aiMessages belong to. Lives here (App stays mounted) so it
  // survives AIInsightsPage unmount/remount on tab switches — lets AI Insights detect that
  // the active project changed WHILE it was unmounted (e.g. after archiving the active
  // project) and reload the correct thread instead of showing the previous project's history.
  const aiMsgOwnerRef = useRef(null);
  const [sessionExpired, setSessionExpired] = useState(false); // Session expiry modal
  const [termsAccepted, setTermsAccepted] = useState(false); // Terms & Disclaimer gate
  const [features, setFeatures] = useState({}); // Resolved feature flags for this user
  const [newVersion, setNewVersion] = useState(null);       // a newer deployed version, if detected
  const [dismissedVersion, setDismissedVersion] = useState(null);

  // Idle enforcement on load: computed synchronously on first render so we never
  // flash protected content. If the last recorded activity is older than the idle
  // window, the restored Amplify session is stale and must be invalidated.
  const [idleExpiredOnLoad] = useState(() => {
    const last = readLastActivity();
    return last > 0 && Date.now() - last > INACTIVITY_MS;
  });

  // If the session was idle-expired before this load, actively sign out (clears the
  // Amplify tokens in localStorage) and reload so the login screen is shown.
  useEffect(() => {
    if (idleExpiredOnLoad) {
      clearLastActivity();
      amplifySignOut().catch(() => {}).finally(() => { window.location.reload(); });
    } else {
      writeLastActivity(Date.now()); // start a fresh activity window for this session
    }
  }, [idleExpiredOnLoad]);

  // Fetch the real Access Token from Cognito for backend API calls
  useEffect(() => {
    async function getToken() {
      try {
        const session = await fetchAuthSession();
        setJwtToken(session.tokens.idToken.toString()); // ID Token safely contains user group claims
      } catch (e) {
        console.error("Failed to fetch auth session", e);
        setSessionExpired(true); // Token refresh failed — session expired
      }
    }
    getToken();
  }, [cognitoUser]);

  // Global 401 handler — no more "soft logout" (blank data on an unresponsive app).
  // When any backend call returns 401 we try ONE silent token refresh: if the session
  // is still valid we swap in a fresh token and the current views recover on their own
  // (their effects re-run on the new idToken); if the refresh fails the session is truly
  // dead, so we force the hard-logout modal and the user re-authenticates.
  const reauthInFlight = useRef(false);
  useEffect(() => {
    setUnauthorizedHandler(async () => {
      if (reauthInFlight.current) return;   // collapse a burst of 401s into one attempt
      reauthInFlight.current = true;
      let recovered = false;
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        const fresh = session.tokens?.idToken?.toString();
        if (fresh) { setJwtToken(fresh); recovered = true; }
      } catch (e) { /* refresh token dead → fall through to hard logout */ }
      if (!recovered) setSessionExpired(true);
      setTimeout(() => { reauthInFlight.current = false; }, 5000);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // Activity beacon / heartbeat — keeps "last active" fresh so the admin Usage page
  // can show real-time presence. Pings on load/login, every 2 min while the tab is
  // visible, and whenever the tab regains visibility. Best-effort (never throws).
  useEffect(() => {
    if (!jwtToken) return;
    const API_URL = (process.env.REACT_APP_API_URL || "").replace(/^["']|["']$/g, "").trim().replace(/\/$/, "");
    const sendPing = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      fetch(`${API_URL}/user/ping`, { method: "POST", headers: { Authorization: `Bearer ${jwtToken}` } }).catch(() => {});
    };
    sendPing(); // immediate on load/login
    const interval = setInterval(sendPing, 2 * 60 * 1000); // heartbeat every 2 min while open
    const onVisible = () => { if (document.visibilityState === "visible") sendPing(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(interval); document.removeEventListener("visibilitychange", onVisible); };
  }, [jwtToken]);

  // Session inactivity timeout (2 hours). Enforced against a PERSISTED last-activity
  // timestamp so it holds up even when the tab is left open and the machine sleeps:
  //   - on every interaction we first CHECK the elapsed idle gap (and expire if the
  //     window already passed) BEFORE resetting — so a click after waking from sleep
  //     logs out instead of silently restarting the timer;
  //   - a periodic backstop + visibility/focus checks catch an open, untouched tab
  //     (JS timers are paused during sleep, so we can't rely on setTimeout alone).
  useEffect(() => {
    if (idleExpiredOnLoad) return; // already signing out
    let timeout;
    let lastPersist = 0;

    const expire = () => {
      clearTimeout(timeout);
      clearLastActivity();       // so a subsequent reload also lands on login
      setSessionExpired(true);   // blocking modal → user must re-authenticate
    };

    // Returns true (and expires) if the idle window has already elapsed.
    const enforce = () => {
      const last = readLastActivity();
      if (last > 0 && Date.now() - last > INACTIVITY_MS) {
        expire();
        return true;
      }
      return false;
    };

    const onActivity = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      if (enforce()) return;              // long-idle already? log out, don't reset
      const now = Date.now();
      if (now - lastPersist > 15000) {    // throttle localStorage writes
        writeLastActivity(now);
        lastPersist = now;
      }
      clearTimeout(timeout);
      timeout = setTimeout(expire, INACTIVITY_MS);
    };

    const onVisibleOrFocus = () => {
      if (document.visibilityState !== "hidden") {
        if (enforce()) return;
        clearTimeout(timeout);
        timeout = setTimeout(expire, INACTIVITY_MS);
      }
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisibleOrFocus);
    window.addEventListener('focus', onVisibleOrFocus);
    // Backstop: even with no interaction, re-check every 30s (also catches wake-from-sleep).
    const interval = setInterval(enforce, 30000);

    writeLastActivity(Date.now());
    lastPersist = Date.now();
    timeout = setTimeout(expire, INACTIVITY_MS);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
      events.forEach(e => window.removeEventListener(e, onActivity));
      document.removeEventListener('visibilitychange', onVisibleOrFocus);
      window.removeEventListener('focus', onVisibleOrFocus);
    };
  }, [idleExpiredOnLoad]);

  // Fetch tenant type once we have a token.
  // IMPORTANT: while this is unresolved (tenantType stays null) MainLayout shows only the
  // safe minimal nav — it must NOT flash the wrong menu. Two hardening rules here:
  //  1) RETRY transient failures (a reload right after the update banner can race token
  //     init / hit a Lambda cold start, and a single failed fetch previously fell straight
  //     through to the fallback).
  //  2) The final fallback is the LEAST-privileged view ("LightUser"), never "FullUser" —
  //     so a failure can never expose features the user shouldn't see (e.g. the old
  //     "Database Setup" flow). A real FullUser simply self-heals on the next successful load.
  useEffect(() => {
    let cancelled = false;
    async function fetchTenantType() {
      const API_URL = (process.env.REACT_APP_API_URL || "").replace(/^["']|["']$/g, "").trim().replace(/\/$/, "");
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          const res = await fetch(`${API_URL}/ai/tenant-config`, {
            headers: { Authorization: `Bearer ${jwtToken}` }
          });
          if (res.ok) {
            const data = await res.json();
            if (!cancelled) setTenantType(data.tenantType || "LightUser");
            return;
          }
        } catch (err) {
          // transient (network / cold start) — fall through to retry
        }
        await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
      }
      if (!cancelled) setTenantType("LightUser"); // safe least-privileged default after retries
    }
    if (jwtToken) fetchTenantType();
    return () => { cancelled = true; };
  }, [jwtToken]);

  // Fetch resolved feature flags for this user (gates new/in-progress UI).
  useEffect(() => {
    if (!jwtToken) return;
    let cancelled = false;
    // Resolve feature flags with a bounded retry. Right after the "new version" banner
    // triggers window.location.reload(), the backend Lambda can be cold-starting, so the
    // first /features call may fail or time out transiently. Without a retry that single
    // failure leaves every flag off — which HIDES gated UI (e.g. the Auto-Insights board)
    // until the user does a hard refresh. Retrying a few times with short backoff, and
    // never overwriting an already-resolved set of flags with an empty one on a later
    // transient failure, closes that gap.
    async function fetchFeatures() {
      const API_URL = (process.env.REACT_APP_API_URL || "").replace(/^["']|["']$/g, "").trim().replace(/\/$/, "");
      const maxAttempts = 4; // ~ up to 0.6s+1.2s+1.8s of backoff on a cold start
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (cancelled) return;
        try {
          const res = await fetch(`${API_URL}/features`, { headers: { Authorization: `Bearer ${jwtToken}` } });
          if (res.ok) {
            const data = await res.json();
            if (!cancelled) setFeatures(data.features || {});
            return; // success — stop retrying
          }
        } catch (err) {
          // Network-level failure (cold start / transient) — fall through to retry.
          console.warn("Failed to fetch feature flags (attempt " + (attempt + 1) + ")", err);
        }
        if (attempt < maxAttempts - 1) {
          await new Promise((r) => setTimeout(r, 600 * (attempt + 1)));
        }
      }
      // All attempts failed — leave any previously-resolved flags untouched (don't blank them).
    }
    fetchFeatures();
    // Re-resolve flags when the tab regains focus/visibility, so an admin toggle (or a flag
    // that resolved before a transient error/outage) takes effect without a hard refresh.
    const onFocus = () => { if (document.visibilityState !== "hidden") fetchFeatures(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [jwtToken]);

  // Detect when a newer version has been deployed and prompt the user to reload.
  // Polls the (cache-busted) /version.json, which is regenerated on every build and
  // refreshed at the CDN on each deploy (the deploy invalidates /*).
  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        if (active && data && data.version && data.version !== APP_VERSION) {
          setNewVersion(data.version);
        }
      } catch (e) { /* offline / transient — ignore */ }
    };
    const first = setTimeout(check, 15000);          // shortly after load
    const interval = setInterval(check, 5 * 60 * 1000); // every 5 minutes
    return () => { active = false; clearTimeout(first); clearInterval(interval); };
  }, []);

  // Safely extract the username whether it's a fresh login or a restored session
  const username = cognitoUser?.signInDetails?.loginId || cognitoUser?.username || "User";
  const appUser = {
    name: username,
    initials: username.charAt(0).toUpperCase(),
    token: jwtToken
  };

  // Decode the ID token to read Cognito group claims. Members of the "admin"
  // (or "superadmin") group can access Settings and onboard new customers/admins.
  const isAdmin = (() => {
    if (!jwtToken) return false;
    try {
      const payload = JSON.parse(atob(jwtToken.split(".")[1]));
      const groups = payload["cognito:groups"] || [];
      return groups.includes("admin") || groups.includes("superadmin");
    } catch (e) {
      return false;
    }
  })();

  // Idle-expired on load: don't render the app — show a minimal overlay while we
  // sign out and reload to the login screen (prevents flashing protected content).
  if (idleExpiredOnLoad) {
    return (
      <div style={{
        position: "fixed", inset: 0, zIndex: 2147483647,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        gap: "16px", background: "#0b1020",
      }}>
        <img src={logo} alt="Boomerang" style={{ height: "52px", objectFit: "contain" }} />
        <span style={{ color: "#94a3b8", fontSize: "14px" }}>Session expired — returning to sign in…</span>
      </div>
    );
  }

  return (
    <FeatureFlagsContext.Provider value={features}>
    <>
    {/* New-version available banner */}
    {newVersion && newVersion !== dismissedVersion && (
      <div style={{
        position: "sticky", top: 0, zIndex: 9998,
        background: "linear-gradient(90deg, #6366f1, #7c3aed)", color: "#fff",
        padding: "9px 16px", display: "flex", alignItems: "center", justifyContent: "center",
        gap: "14px", fontSize: "13.5px", fontWeight: 600, boxShadow: "0 2px 10px rgba(0,0,0,.15)",
      }}>
        <span>✨ A new version (v{newVersion}) of Boomerang is available.</span>
        <button onClick={() => window.location.reload()} style={{
          background: "#fff", color: "#4f46e5", border: "none", borderRadius: "8px",
          padding: "5px 14px", fontWeight: 700, cursor: "pointer", fontSize: "13px",
        }}>Reload</button>
        <button onClick={() => setDismissedVersion(newVersion)} style={{
          background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,.55)",
          borderRadius: "8px", padding: "5px 12px", cursor: "pointer", fontSize: "13px",
        }}>Later</button>
      </div>
    )}
    {/* Terms & Disclaimer acceptance gate — blocks the app on first login until accepted */}
    {jwtToken && !termsAccepted && (
      <TermsGate idToken={jwtToken} onAccepted={() => setTermsAccepted(true)} />
    )}
    {/* Session expired modal */}
    {sessionExpired && (
      <div style={{
        position: "fixed", inset: 0, zIndex: 99999,
        background: "rgba(0,0,0,0.6)", display: "flex",
        alignItems: "center", justifyContent: "center",
        backdropFilter: "blur(4px)",
      }}>
        <div style={{
          background: "#fff", borderRadius: "16px", padding: "32px",
          maxWidth: "380px", width: "90%", textAlign: "center",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}>
          <div style={{ fontSize: "40px", marginBottom: "12px" }}>🔒</div>
          <h3 style={{ margin: "0 0 8px", fontSize: "18px", fontWeight: 700, color: "#0f172a" }}>
            Session Expired
          </h3>
          <p style={{ fontSize: "14px", color: "#64748b", margin: "0 0 24px", lineHeight: 1.5 }}>
            Your session has expired due to inactivity. Please log in again to continue.
          </p>
          <button
            onClick={signOut}
            style={{
              padding: "12px 32px", borderRadius: "10px", fontSize: "14px", fontWeight: 600,
              border: "none", background: "linear-gradient(135deg, #6366f1, #4f46e5)",
              color: "#fff", cursor: "pointer",
              boxShadow: "0 4px 12px rgba(99,102,241,0.3)",
            }}
          >
            Log In Again
          </button>
        </div>
      </div>
    )}
    {/* UploadQueueProvider is OUTSIDE ProjectProvider on purpose: ProjectContext consumes the
        upload queue to refuse a project switch while a batch is importing (a switch mid-batch
        was silently scattering one upload's files across projects). It holds only useState and
        depends on nothing, so hoisting it is inert for every other consumer. */}
    <UploadQueueProvider>
    <ProjectProvider userKey={username} enabled={!!jwtToken && !sessionExpired}>
    <BoardActivityProvider>
    <BlueprintImportProvider>
    <MainLayout
      user={appUser}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      onLogout={signOut}
      tenantType={tenantType}
      isSuperAdmin={isAdmin}
    >
      {activeTab === "dashboard"      && <AIInsightsPage idToken={appUser.token} messages={aiMessages} setMessages={setAiMessages} msgOwnerRef={aiMsgOwnerRef} />}
      {activeTab === "ai-dashboard"   && <DashboardPage idToken={appUser.token} onGoAnalyze={() => setActiveTab("dashboard")} />}
      {activeTab === "s3-upload"      && <S3UploadPage idToken={appUser.token} />}
      {activeTab === "database-setup" && tenantType === "FullUser" && <DatabaseSetupPage idToken={appUser.token} setActiveTab={setActiveTab} onTabChange={setActiveTab} />}
      {activeTab === "data-upload"    && tenantType === "FullUser" && <DataUploadPage setActiveTab={setActiveTab} onTabChange={setActiveTab} />}
      {activeTab === "admin"          && isAdmin && <AdminPage idToken={appUser.token} onClose={() => setActiveTab("dashboard")} />}
      {activeTab === "security"       && <SecurityPage userEmail={username} />}
    </MainLayout>
    </BlueprintImportProvider>
    </BoardActivityProvider>
    </ProjectProvider>
    </UploadQueueProvider>
    <FeedbackWidget idToken={appUser.token} currentPage={activeTab} />
    </>
    </FeatureFlagsContext.Provider>
  );
}
